/**
 * Run the palm pipeline — in a Worker where there is one, on the main thread
 * where there is not. Both give the same answer; only the responsiveness differs.
 *
 * © Manish Jagdish Thatte
 */

import { analyse } from './pipeline.js';

let worker = null; let seq = 0; const pending = new Map();

function getWorker() {
  if (worker !== null) return worker;
  worker = false;
  if (typeof Worker === 'function') {
    try {
      worker = new Worker(new URL('./pipeline.worker.js', import.meta.url), { type: 'module' });
      worker.addEventListener('message', (e) => {
        const p = pending.get(e.data.id); if (!p) return; pending.delete(e.data.id);
        if (e.data.error) p.reject(new Error(e.data.error)); else p.resolve(e.data.result);
      });
      worker.addEventListener('error', () => {
        // a worker that fails to start is abandoned; callers retry on the main thread
        for (const p of pending.values()) p.reject(new Error('worker-failed'));
        pending.clear(); worker = false;
      });
    } catch { worker = false; }
  }
  return worker;
}

/** Analyse `{ width, height, data }` RGBA. `data` is copied when a Worker is used (the caller keeps its pixels). */
export async function runPipeline(image, opts = {}) {
  const w = getWorker();
  if (w) {
    try {
      const id = ++seq; const copy = image.data.slice().buffer;
      const p = new Promise((resolve, reject) => pending.set(id, { resolve, reject }));
      w.postMessage({ id, image: { width: image.width, height: image.height, buffer: copy }, opts }, [copy]);
      return await p;
    } catch { /* fall through to the main thread */ }
  }
  return analyse(image, opts);
}
